import { csvCell } from './csv'
import { nodeDisplayName } from '@routr/shared'
import { slotDescriptor, type DeviceCategory } from '@routr/shared'
import { SIGNAL_CATEGORIES, SIGNAL_META, type SignalCategory } from '@routr/shared'

/**
 * The run sheet (patch list) is the document the rigging crew works from:
 * one row per cable, source → destination, grouped by signal. This module is
 * pure so the HTML page, the CSV and the PDF all render the same rows.
 */

export type RunSheetRow = {
  cableId: string
  /** True when the id was generated because the edge has no label. */
  cableIdIsAuto: boolean
  signal: SignalCategory
  fromDevice: string
  fromModel: string
  fromCategory: DeviceCategory
  fromUnit: string | null
  fromPort: string
  fromConnector: string
  /** "Slot 2 · DeckLink 8K Pro G2" when the port is an installed card's; null for onboard ports. */
  fromSlot: string | null
  toPort: string
  toConnector: string
  toSlot: string | null
  toDevice: string
  toModel: string
  toCategory: DeviceCategory
  toUnit: string | null
  /** Run length in metres, or null where nobody has measured it. */
  lengthM: number | null
}

export type RunSheetSection = {
  signal: SignalCategory
  label: string
  rows: RunSheetRow[]
}

export type RunSheet = {
  project: { id: string; name: string; description: string | null }
  workspace: { name: string; slug: string }
  generatedAt: string
  sections: RunSheetSection[]
  totals: { cables: number; devices: number; unassignedUnits: number }
  /** Placed devices with no cable at all — worth a second look before the show. */
  unpatchedDevices: string[]
}

export type RunSheetNodeInput = {
  id: string
  label: string | null
  deviceName: string
  deviceModel: string
  deviceCategory: DeviceCategory
  unit: string | null
}

export type RunSheetPortInput = {
  name: string
  connector: string
  signal: SignalCategory
  position: number
  /** The computer slot this port was copied from; null for onboard ports. */
  slot: { label: string; cardModel: string | null } | null
}

export type RunSheetEdgeInput = {
  id: string
  label: string | null
  lengthM: number | null
  sourceNodeId: string
  targetNodeId: string
  sourcePort: RunSheetPortInput
  targetPort: RunSheetPortInput
}

const PREFIX: Record<SignalCategory, string> = { VIDEO: 'V', AUDIO: 'A', NETWORK: 'N', DATA: 'D', POWER: 'P' }

const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

const slotOf = (port: RunSheetPortInput) =>
  port.slot ? slotDescriptor(port.slot.label, port.slot.cardModel) : null

export function buildRunSheet(input: {
  project: { id: string; name: string; description: string | null }
  workspace: { name: string; slug: string }
  nodes: RunSheetNodeInput[]
  edges: RunSheetEdgeInput[]
  now?: Date
}): RunSheet {
  const nodeById = new Map(input.nodes.map((node) => [node.id, node]))
  const displayName = (node: RunSheetNodeInput) => nodeDisplayName(node.label, node.deviceName)

  const sections: RunSheetSection[] = SIGNAL_CATEGORIES.map((signal) => {
    const edges = input.edges
      .filter((edge) => edge.sourcePort.signal === signal)
      .sort((a, b) => {
        const fromA = nodeById.get(a.sourceNodeId)
        const fromB = nodeById.get(b.sourceNodeId)
        // Port positions restart inside each slot, so the slot is compared before
        // them; '' sorts first, which puts onboard ports ahead of Slot 1, Slot 2 …
        return (
          natural(fromA ? displayName(fromA) : '', fromB ? displayName(fromB) : '') ||
          natural(a.sourcePort.slot?.label ?? '', b.sourcePort.slot?.label ?? '') ||
          a.sourcePort.position - b.sourcePort.position ||
          natural(a.sourcePort.name, b.sourcePort.name) ||
          natural(a.targetPort.name, b.targetPort.name)
        )
      })

    // Explicit labels win; the rest are numbered in sheet order so the ids on
    // the paper match the order the crew reads them.
    let sequence = 0
    const taken = new Set(edges.map((edge) => edge.label?.trim()).filter(Boolean) as string[])
    const rows: RunSheetRow[] = []
    for (const edge of edges) {
      const from = nodeById.get(edge.sourceNodeId)
      const to = nodeById.get(edge.targetNodeId)
      if (!from || !to) continue
      let cableId = edge.label?.trim() ?? ''
      let auto = false
      if (!cableId) {
        do {
          sequence += 1
          cableId = `${PREFIX[signal]}-${String(sequence).padStart(3, '0')}`
        } while (taken.has(cableId))
        auto = true
      }
      rows.push({
        cableId,
        cableIdIsAuto: auto,
        signal,
        fromDevice: displayName(from),
        fromModel: from.deviceModel,
        fromCategory: from.deviceCategory,
        fromUnit: from.unit,
        fromPort: edge.sourcePort.name,
        fromConnector: edge.sourcePort.connector,
        fromSlot: slotOf(edge.sourcePort),
        toPort: edge.targetPort.name,
        toConnector: edge.targetPort.connector,
        toSlot: slotOf(edge.targetPort),
        toDevice: displayName(to),
        toModel: to.deviceModel,
        toCategory: to.deviceCategory,
        lengthM: edge.lengthM,
        toUnit: to.unit,
      })
    }
    return { signal, label: SIGNAL_META[signal].label, rows }
  }).filter((section) => section.rows.length > 0)

  const patched = new Set<string>()
  for (const edge of input.edges) {
    patched.add(edge.sourceNodeId)
    patched.add(edge.targetNodeId)
  }
  const unpatchedDevices = input.nodes
    .filter((node) => !patched.has(node.id))
    .map(displayName)
    .sort(natural)

  return {
    project: input.project,
    workspace: input.workspace,
    generatedAt: (input.now ?? new Date()).toISOString(),
    sections,
    totals: {
      cables: sections.reduce((sum, section) => sum + section.rows.length, 0),
      devices: input.nodes.length,
      unassignedUnits: input.nodes.filter((node) => !node.unit).length,
    },
    unpatchedDevices,
  }
}

export const CSV_COLUMNS = [
  'Cable ID',
  'Signal',
  'From device',
  'From unit',
  'From port',
  'From connector',
  'To port',
  'To connector',
  'To device',
  'To unit',
  // Appended, never inserted: the first ten positions stay stable for saved
  // spreadsheet mappings.
  'From slot',
  'To slot',
  'Length (m)',
] as const

/** UTF-8 with BOM and CRLF so Excel opens it without an import dialog. */
export function runSheetToCsv(sheet: RunSheet): string {
  const lines = [CSV_COLUMNS.join(',')]
  for (const section of sheet.sections) {
    for (const row of section.rows) {
      lines.push(
        [
          row.cableId,
          section.label,
          row.fromDevice,
          row.fromUnit,
          row.fromPort,
          row.fromConnector,
          row.toPort,
          row.toConnector,
          row.toDevice,
          row.toUnit,
          row.fromSlot,
          row.toSlot,
          // Empty, never 0, when nobody has measured it — a zero would sum in
          // a spreadsheet and read as a cable that needs no length.
          row.lengthM == null ? null : String(row.lengthM),
        ]
          .map(csvCell)
          .join(','),
      )
    }
  }
  return '﻿' + lines.join('\r\n') + '\r\n'
}

/** Safe file-name stem for downloads: "OB Truck 2 — Rugby" → "ob-truck-2-rugby". */
export function fileStem(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'project'
  )
}
